import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ActionEnum, Role, Uni } from '@vendia/management-api-types'
import { UpdateRoleDocument } from '@vendia/control-plane-api'
import { useState } from 'react'
import Button from 'src/components/buttons/button'
import Card from 'src/components/containers/card'
import Icon from 'src/components/icons/icon'
import CheckboxInput from 'src/components/inputs/checkbox.input'
import ListboxInput from 'src/components/inputs/listbox.input'
import BasicModal from 'src/components/modals/modal.basic'
import ConfirmationModal from 'src/components/modals/confirmation-modal'
import { Table } from 'src/types/types'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { useGetOrgDetails } from 'src/utils/hooks/use-get-org-details'
import useRolePermits from 'src/utils/hooks/use-role-permits'
import notify from 'src/utils/notify'
import {
  addTableAccessToRole,
  buildResource,
  buildTableMetadataResource,
  getAccessSourceLabel,
  getRolesWithResourceAccess,
  hasSpecificMetadataAccess,
  normalizeCapabilitiesForApi,
  removeTableAccessFromRole,
  RoleWithResourceAccess,
  TABLE_ACTIONS,
} from 'src/utils/rbac/resource-role-access'

interface RoleAccessCardProps {
  uni: Uni
  nodeName: string
  table: Table
}

export const RoleAccessCard = ({ uni, nodeName, table }: RoleAccessCardProps) => {
  const client = useControlPlaneApi()
  const queryClient = useQueryClient()
  const getOrgDetails = useGetOrgDetails()
  const org = getOrgDetails?.data?.getOrganization

  const hasRoleUpdate = useRolePermits({ action: ActionEnum.RoleUpdate })

  const [showGrantModal, setShowGrantModal] = useState(false)
  const [showRevokeModal, setShowRevokeModal] = useState(false)
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null)
  const [roleToRevoke, setRoleToRevoke] = useState<RoleWithResourceAccess | null>(null)
  const [alsoRemoveMetadataAccess, setAlsoRemoveMetadataAccess] = useState(false)

  // Build the table resource pattern
  const tableResource = buildResource({
    orgId: uni.orgId ?? '',
    uniName: uni.name,
    nodeName,
    entityName: table.name,
  })

  // Build the metadata resource pattern (for Vendia_Table)
  const metadataResource = buildTableMetadataResource({
    orgId: uni.orgId ?? '',
    uniName: uni.name,
    nodeName,
  })

  // Get all active roles from org
  const allRoles = (org?.roles?.filter((role) => !role.deactivated) ?? []) as Role[]

  // Get roles that have access to this table (sorted alphabetically)
  const rolesWithAccess = getRolesWithResourceAccess(allRoles, tableResource, TABLE_ACTIONS).sort((a, b) =>
    (a.role.name ?? '').localeCompare(b.role.name ?? ''),
  )

  // Get roles that don't have access (for the grant modal, sorted alphabetically)
  const rolesWithoutAccess = allRoles
    .filter((role) => !rolesWithAccess.some((r) => r.role.roleId === role.roleId))
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))

  // Check if the role being revoked has specific Vendia_Table DATA_READ permission
  const roleToRevokeHasMetadataAccess = roleToRevoke
    ? hasSpecificMetadataAccess(roleToRevoke.role.capabilities ?? [], metadataResource)
    : false

  const grantAccessMutation = useMutation({
    mutationFn: async ({ roleId }: { roleId: string }) => {
      const role = allRoles.find((r) => r.roleId === roleId)
      if (!role) throw new Error('Role not found')

      const updatedCapabilities = addTableAccessToRole(role.capabilities ?? [], tableResource)

      const result = await client.graphqlFetch({ query: UpdateRoleDocument, variables: {
        roleId,
        name: role.name,
        capabilities: normalizeCapabilitiesForApi(updatedCapabilities),
      } })

      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }

      return result
    },
    onError: (error) => notify.error(`Error granting access: ${error}`),
    onSuccess: () => {
      notify.success(`Access granted successfully`)
      setShowGrantModal(false)
      setSelectedRoleId(null)
      queryClient.invalidateQueries()
    },
  })

  const revokeAccessMutation = useMutation({
    mutationFn: async ({ roleId, removeMetadata }: { roleId: string; removeMetadata: boolean }) => {
      const role = allRoles.find((r) => r.roleId === roleId)
      if (!role) throw new Error('Role not found')

      const updatedCapabilities = removeTableAccessFromRole(role.capabilities ?? [], tableResource, {
        alsoRemoveMetadataAccess: removeMetadata,
      })

      const result = await client.graphqlFetch({ query: UpdateRoleDocument, variables: {
        roleId,
        name: role.name,
        capabilities: normalizeCapabilitiesForApi(updatedCapabilities),
      } })

      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }

      return result
    },
    onError: (error) => notify.error(`Error revoking access: ${error}`),
    onSuccess: () => {
      notify.success(`Access revoked successfully`)
      setShowRevokeModal(false)
      setRoleToRevoke(null)
      setAlsoRemoveMetadataAccess(false)
      queryClient.invalidateQueries()
    },
  })

  // Don't show card if user doesn't have permission to update roles
  if (!hasRoleUpdate) {
    return null
  }

  if (getOrgDetails.isLoading) {
    return (
      <Card className='w-full'>
        <div className='animate-pulse'>
          <div className='mb-2 h-4 w-24 rounded bg-gray-200' />
          <div className='h-20 rounded bg-gray-100' />
        </div>
      </Card>
    )
  }

  return (
    <>
      <Card className='w-full'>
        <div className='space-y-4'>
          <div className='flex items-center justify-between'>
            <h4 className='text-sm font-bold'>Access Grants</h4>
            <Button
              kind='icon'
              size='small'
              icon='plus-m'
              label='Grant access to role'
              onClick={() => setShowGrantModal(true)}
              disabled={rolesWithoutAccess.length === 0}
            />
          </div>

          {rolesWithAccess.length === 0 ? (
            <p className='text-neutral-6 text-sm'>No roles have access to this table.</p>
          ) : (
            <div className='max-h-64 space-y-2 overflow-y-auto'>
              {rolesWithAccess.map(({ role, accessInfo }) => (
                <div
                  key={role.roleId}
                  className='bg-uibg-0 flex items-center justify-between rounded-md border border-gray-200 p-2'
                >
                  <div className='max-w-72 truncate'>
                    <div className='truncate text-sm font-medium'>{role.name}</div>
                    <div className='text-neutral-7 text-xs'>{getAccessSourceLabel(accessInfo.source, 'table')}</div>
                  </div>
                  {accessInfo.canRevoke && (
                    <Button
                      kind='icon'
                      size='small'
                      icon='x'
                      label='Revoke access'
                      onClick={() => {
                        setRoleToRevoke({ role, accessInfo })
                        setShowRevokeModal(true)
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          <p className='text-neutral-6 text-xs'>
            {rolesWithAccess.length} role{rolesWithAccess.length !== 1 ? 's' : ''} can query this table
          </p>
        </div>
      </Card>

      {/* Grant Access Modal */}
      <BasicModal
        isOpen={showGrantModal}
        onClose={() => {
          setShowGrantModal(false)
          setSelectedRoleId(null)
        }}
        title='Grant table access'
        contentClasses='overflow-visible'
        buttons={
          <>
            <Button
              kind='secondary'
              onClick={() => {
                setShowGrantModal(false)
                setSelectedRoleId(null)
              }}
            >
              Cancel
            </Button>
            <Button
              kind='primary'
              onClick={() => {
                if (!selectedRoleId) {
                  notify.error('Please select a role')
                  return
                }
                grantAccessMutation.mutate({ roleId: selectedRoleId })
              }}
              disabled={grantAccessMutation.isPending || !selectedRoleId}
              icon={grantAccessMutation.isPending ? 'refresh' : undefined}
              iconProps={{ isSpinning: grantAccessMutation.isPending, size: 15 }}
            >
              Grant Access
            </Button>
          </>
        }
      >
        <div className='space-y-4'>
          <p className='text-sm'>
            Select a role to grant query access to <strong>{table.name}</strong>.
          </p>
          <ListboxInput
            // Fixes weird interaction between dropdown and modal causing scroll issues
            optionsClassName='!max-h-[350px]'
            name='role'
            placeholder='Select a role'
            value={selectedRoleId ?? undefined}
            onChange={(option) => setSelectedRoleId(option.value)}
            options={rolesWithoutAccess.map((role) => ({
              value: role.roleId!,
              label: role.name!,
            }))}
          />
          <p className='text-neutral-6 text-xs'>
            <Icon name='info' size={12} className='mr-1 inline' />
            This will grant DATA_READ permission for all tables (required for table schema/metadata access) and
            TABLE_ALL permission for querying this specific table.
          </p>
        </div>
      </BasicModal>

      {/* Revoke Access Modal */}
      <ConfirmationModal
        isOpen={showRevokeModal}
        onClose={() => {
          setShowRevokeModal(false)
          setRoleToRevoke(null)
          setAlsoRemoveMetadataAccess(false)
        }}
        title='Revoke table access'
        onSubmit={() => {
          if (!roleToRevoke) return
          revokeAccessMutation.mutate({
            roleId: roleToRevoke.role.roleId!,
            removeMetadata: alsoRemoveMetadataAccess,
          })
        }}
        isSubmitting={revokeAccessMutation.isPending}
        actionButtonType='danger'
        actionButtonText='Revoke Access'
        confirmationText='Revoke'
      >
        <div className='space-y-4'>
          <p className='text-sm'>
            This will revoke query access to <strong>{table.name}</strong> from the{' '}
            <strong>{roleToRevoke?.role.name}</strong> role. Are you sure you want to proceed?
          </p>

          {roleToRevokeHasMetadataAccess && (
            <CheckboxInput
              name='alsoRemoveMetadataAccess'
              label='Also remove table metadata access from this role'
              description={`This will remove the role's DATA_READ permission for Vendia tables which allows the role to discover tables and view their schemas. Only select this if you are sure the role should not have access to any Vendia table metadata.`}
              checked={alsoRemoveMetadataAccess}
              onChange={() => setAlsoRemoveMetadataAccess(!alsoRemoveMetadataAccess)}
            />
          )}
        </div>
      </ConfirmationModal>
    </>
  )
}
